/**
 * Accounts contract: org signup, the signed-in user's memberships, members and invitations, API
 * keys and the onboarding checklist. Roles: owner > admin > engineer / product > viewer;
 * underwriter is a separate read-only role for carrier partners.
 */
import type { SignupBody } from "./schemas";
import type { ApiKey, ISODate, Org } from "./types";

export type Role = Org["user"]["role"];

/** Who is signed in and which orgs they belong to (GET /api/v1/me works before an org exists). */
export interface Me {
  email: string;
  name: string;
  orgs: Array<{ id: string; name: string; slug: string; role: Role }>;
  /** Org the request resolved to (null = no membership yet → signup, or several and none selected). */
  currentOrgId: string | null;
  /** Pending invitations addressed to this email. */
  invitations: Array<{ id: string; orgName: string; role: Role; invitedBy: string; expiresAt: ISODate }>;
}

export interface Member {
  userId: string;
  name: string;
  email: string;
  role: Role;
  joinedAt: ISODate;
  lastSeenAt: ISODate | null;
}

export interface Invitation {
  id: string;
  email: string;
  role: Role;
  invitedBy: string;
  createdAt: ISODate;
  expiresAt: ISODate;
  status: "pending" | "accepted" | "declined" | "revoked" | "expired";
  /** Only on the create response: whether the invitation email was handed to the mail service. */
  emailSent?: boolean;
}

/** Returned once on creation: the full key is never shown again (only its hash is stored). */
export interface ApiKeyCreated {
  key: ApiKey;
  secret: string;
}

export interface OnboardingStep {
  id: "api_key" | "first_trace" | "first_session" | "invite_team" | "profile_review" | "notifications";
  label: string;
  description: string;
  done: boolean;
  href: string;
}

export interface Onboarding {
  steps: OnboardingStep[];
  complete: boolean;
}

// ─── Role matrix ─────────────────────────────────────────────────────────────
//
// Every role reads everything in its org (underwriters see the same API; the UI scopes them to the
// portfolio). Writes are split three ways:
//
//   capability        what it covers                                                    roles
//   ───────────────── ───────────────────────────────────────────────────────────────── ─────────────────────────────
//   read              every GET, ask-your-data                                          all
//   write:analytics   funnels, use cases, pain points, insights, signal definitions     owner admin engineer product
//                     (draft, lifecycle, backtest) and review-queue labels
//   write             issues, risk-profile tool                                         owner admin engineer
//                     review, coverage (quotes, binding, claims), connectors,
//                     notification channels, settings and enrichment settings
//   admin             API keys, members, invitations                                    owner admin
//
// Role changes: you can't grant a role ranked above your own, nor change or remove a member ranked
// above you (admins can't touch owners). Only owners make owners. The last owner can't be demoted or
// removed (they must promote someone first). Any member may leave (remove themselves) unless they
// are the last owner.

/**
 * `underwrite` is the carrier side: deciding and paying program claims. Only the underwriter role
 * holds it, and only traces.insure operations grant that role (infra/ops/underwriter.mjs), so a
 * policyholder can never decide its own claims.
 */
export type Capability = "read" | "write:analytics" | "write" | "admin" | "underwrite";

export const ROLES: readonly Role[] = ["owner", "admin", "engineer", "product", "viewer", "underwriter"];

export const ROLE_CAPABILITIES: Record<Role, readonly Capability[]> = {
  owner: ["read", "write:analytics", "write", "admin"],
  admin: ["read", "write:analytics", "write", "admin"],
  engineer: ["read", "write:analytics", "write"],
  product: ["read", "write:analytics"],
  viewer: ["read"],
  underwriter: ["read", "underwrite"],
};

/** Rank for escalation checks: owner > admin > engineer = product > viewer = underwriter. */
export const ROLE_RANK: Record<Role, number> = { owner: 4, admin: 3, engineer: 2, product: 2, viewer: 1, underwriter: 1 };

export const roleCan = (role: Role, capability: Capability): boolean => ROLE_CAPABILITIES[role].includes(capability);

/** Can `actor` give someone the role `next`? Underwriters are added by traces.insure operations only. */
export const canGrantRole = (actor: Role, next: Role): boolean => next !== "underwriter" && roleCan(actor, "admin") && ROLE_RANK[next] <= ROLE_RANK[actor];

/** Can `actor` change or remove a member who currently holds `target`? */
export const canManageMember = (actor: Role, target: Role): boolean => roleCan(actor, "admin") && ROLE_RANK[target] <= ROLE_RANK[actor];

/** Roles `actor` may hand out in an invitation (owners are made by promotion, never invited). */
export const invitableRoles = (actor: Role): Role[] => ROLES.filter((r) => r !== "owner" && canGrantRole(actor, r));

// ─── Account rules shared by the mock and live implementations ───────────────

export type AccountErrorCode =
  | "last_owner"
  | "role_escalation"
  | "already_member"
  | "already_invited"
  | "invitation_not_found"
  | "invitation_email_mismatch"
  | "invitation_expired"
  | "invitation_not_pending"
  | "slug_taken"
  | "limit_reached"
  | "project_not_found";

const ACCOUNT_ERROR_STATUS: Record<AccountErrorCode, 403 | 404 | 409 | 422> = {
  last_owner: 409,
  role_escalation: 403,
  already_member: 409,
  already_invited: 409,
  invitation_not_found: 404,
  invitation_email_mismatch: 403,
  invitation_expired: 409,
  invitation_not_pending: 409,
  slug_taken: 409,
  limit_reached: 409,
  project_not_found: 422,
};

/** A broken account rule; the API Worker maps it to problem+json with `status` and `code`. */
export class AccountError extends Error {
  readonly status: 403 | 404 | 409 | 422;
  readonly code: AccountErrorCode;
  constructor(code: AccountErrorCode, message: string) {
    super(message);
    this.code = code;
    this.status = ACCOUNT_ERROR_STATUS[code];
  }
}

export const INVITATION_TTL_MS = 7 * 86_400_000;
export const MAX_ACTIVE_API_KEYS = 50;
export const MAX_PENDING_INVITATIONS = 100;
/** Orgs one identity may create per rolling 24 hours (abuse guard; normal use is one or two). */
export const MAX_SIGNUPS_PER_DAY = 10;

/** Slugs that collide with dashboard routes or are confusing on public pages. */
export const RESERVED_SLUGS = new Set(["admin", "api", "app", "assets", "dashboard", "help", "insured", "internal", "join", "login", "protect", "public", "settings", "setup", "signup", "static", "support", "traces", "www"]);
export const SLUG_PATTERN = /^[a-z0-9](?:[a-z0-9-]{0,48}[a-z0-9])?$/;

/** URL slug from an org name: lowercase ASCII, dashes, at most 50 characters ("" when nothing usable). */
export function slugify(name: string): string {
  return name
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 50)
    .replace(/-+$/g, "");
}

/** Slug candidates in preference order: the base, then base-2 … base-9 (callers add a random suffix past that). */
export function slugCandidates(base: string): string[] {
  const b = base.length >= 2 && !RESERVED_SLUGS.has(base) ? base : `${base || "org"}-team`.replace(/^-/, "");
  return [b, ...Array.from({ length: 8 }, (_, i) => `${b.slice(0, 47)}-${i + 2}`)];
}

const BASE62 = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";
/** Web Crypto (Workers, browsers, Node 20+); typed locally because this package has no DOM / Workers lib. */
const webCrypto = (globalThis as unknown as { crypto: { getRandomValues<T extends Uint8Array>(a: T): T } }).crypto;

/** `n` base62 characters from crypto.getRandomValues (rejection sampling: no modulo bias). */
export function randomBase62(n: number): string {
  let out = "";
  while (out.length < n) {
    for (const b of webCrypto.getRandomValues(new Uint8Array(n * 2))) {
      if (b < 248) out += BASE62[b % 62];
      if (out.length === n) break;
    }
  }
  return out;
}

/** A new API key secret: `ti_live_` / `ti_test_` plus 32 base62 characters. */
export const newApiKeySecret = (environment: "live" | "test"): string => `ti_${environment}_${randomBase62(32)}`;

/** The part of a key that is stored and shown in lists. */
export const apiKeyPrefix = (secret: string): string => secret.slice(0, 12);

/** Display name for an email address (`jane.doe@x.com` → `Jane Doe`). */
export function nameFromEmail(email: string): string {
  const local = email.split("@")[0] ?? email;
  const words = local.split(/[._+-]+/).filter(Boolean);
  return words.length ? words.map((w) => w[0]!.toUpperCase() + w.slice(1)).join(" ") : email;
}

export interface OnboardingFacts {
  apiKey: boolean;
  firstTrace: boolean;
  firstSession: boolean;
  inviteTeam: boolean;
  profileReview: boolean;
  notifications: boolean;
}

/** The checklist, in order, with copy and the page each step links to. */
export function onboardingFrom(f: OnboardingFacts): Onboarding {
  const steps: OnboardingStep[] = [
    { id: "api_key", label: "Create an API key", description: "An ingest key authenticates the traces your agents and their platform send.", done: f.apiKey, href: "/settings?tab=api-keys" },
    { id: "first_trace", label: "Send your first trace", description: "Instrument an agent with an SDK or OpenTelemetry, or connect its agent platform.", done: f.firstTrace, href: "/setup" },
    { id: "first_session", label: "See your first session", description: "End a session (or let it go idle) and it's enriched with use case, outcome and friction.", done: f.firstSession, href: "/sessions" },
    { id: "profile_review", label: "Review an agent's risk profile", description: "Confirm how its tools are classified so detectors and suggested controls use your judgment.", done: f.profileReview, href: "/agents" },
    { id: "invite_team", label: "Invite your team", description: "Add engineers, product managers and admins to this workspace.", done: f.inviteTeam, href: "/settings?tab=members" },
    { id: "notifications", label: "Connect a notification channel", description: "Send issues and coverage changes to Slack, email or a webhook.", done: f.notifications, href: "/settings?tab=notifications" },
  ];
  return { steps, complete: steps.every((s) => s.done) };
}

// ─── Identity-level directory (signup, /me, invitation acceptance) ───────────
//
// These run before the request has an org, so they sit outside the org-scoped DataSource. The API
// Worker implements this over D1 (apps/web/worker/live/accounts.ts).

/** A verified person (a signed-in session, or the dev identity). Emails are lowercase. */
export interface Identity {
  userId: string;
  email: string;
  name: string;
}

export interface Membership {
  orgId: string;
  orgName: string;
  slug: string;
  role: Role;
  /** members.user_id of this membership (may predate the identity's current Access sub). */
  userId: string;
  lastSeenAt: number | null;
}

export interface AccountDirectory {
  memberships(identity: Identity): Promise<Membership[]>;
  pendingInvitations(email: string): Promise<Me["invitations"]>;
  /** Orgs this identity created since `sinceMs` (signup abuse guard). */
  signupsSince(identity: Identity, sinceMs: number): Promise<number>;
  /** Creates the org, its default project, the owner membership and default enrichment settings. */
  signup(identity: Identity, body: SignupBody): Promise<{ orgId: string }>;
  /** Throws AccountError (not found / email mismatch / expired / not pending). Idempotent for existing members. */
  acceptInvitation(identity: Identity, id: string): Promise<{ orgId: string }>;
  declineInvitation(identity: Identity, id: string): Promise<void>;
  /** Record activity (members.last_seen_at). Callers throttle. */
  touch(orgId: string, userId: string, now: number): Promise<void>;
}
